import {
  BarChart3,
  FileSpreadsheet,
  Factory,
  Receipt,
  TrendingUp,
  UserCheck,
  Users,
  Wallet,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { PlanModule, UserRole } from '@tailoring-crm/shared';
import { PageHeader } from '../components/layout/page-header.js';
import { Badge } from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import { useHasModule } from '../lib/billing/entitlements.js';

/**
 * The index of everything the shop can be told about itself.
 *
 * The five reports task 26 builds sit at the top; the three that already existed — the P&L, the
 * expense book and the sales register — sit below, because a screen nothing links to is a screen
 * nobody finds.
 *
 * Each entry declares the roles it is for, and one that is not for the caller is not rendered.
 * Not greyed out, not shown-then-refused: a manager should never tap "Financial" and be told they
 * are not allowed, because the tap was the product's suggestion in the first place.
 *
 * A report outside the shop's PLAN is the opposite case, and is shown with a badge: that tap
 * leads to what the report is and what it would cost, which is a question the owner can answer.
 */

interface ReportLink {
  to: string;
  icon: typeof FileSpreadsheet;
  titleKey: string;
  hintKey: string;
  roles: readonly UserRole[];
  /** The plan module the report needs, per role — `core` when omitted. */
  module?: (role: UserRole) => PlanModule;
}

const PRO_ONLY = (): PlanModule => 'advanced_analytics';

const EVERYONE_BUT_TAILORS: readonly UserRole[] = ['owner', 'manager'];
const OWNER_ONLY: readonly UserRole[] = ['owner'];

const REPORTS: readonly ReportLink[] = Object.freeze([
  {
    to: '/reports/sales',
    icon: TrendingUp,
    titleKey: 'reports.sales.title',
    hintKey: 'reports.sales.hint',
    roles: EVERYONE_BUT_TAILORS,
  },
  {
    to: '/reports/production',
    icon: Factory,
    titleKey: 'reports.production.title',
    hintKey: 'reports.production.hint',
    module: PRO_ONLY,
    roles: EVERYONE_BUT_TAILORS,
  },
  {
    to: '/reports/staff',
    icon: UserCheck,
    titleKey: 'reports.staff.title',
    hintKey: 'reports.staff.hint',
    // A karigar reaches this one, and sees their own figures. See `staff-report-page.tsx`.
    roles: ['owner', 'manager', 'tailor'],
    // Their own figures come with the karigar floor; the whole shop's table is Pro's.
    module: (role: UserRole): PlanModule =>
      role === 'tailor' ? 'workforce' : 'advanced_analytics',
  },
  {
    to: '/reports/customers',
    icon: Users,
    titleKey: 'reports.customers.title',
    hintKey: 'reports.customers.hint',
    module: PRO_ONLY,
    roles: EVERYONE_BUT_TAILORS,
  },
  {
    to: '/reports/financial',
    icon: Wallet,
    titleKey: 'reports.financial.title',
    hintKey: 'reports.financial.hint',
    module: PRO_ONLY,
    roles: OWNER_ONLY,
  },
  {
    to: '/reports/profit-and-loss',
    icon: BarChart3,
    titleKey: 'reports.pnlLink',
    hintKey: 'reports.pnlHint',
    roles: OWNER_ONLY,
  },
  {
    to: '/expenses',
    icon: Receipt,
    titleKey: 'reports.expensesLink',
    hintKey: 'reports.expensesHint',
    roles: EVERYONE_BUT_TAILORS,
  },
  {
    to: '/reports/sales-register',
    icon: FileSpreadsheet,
    titleKey: 'reports.salesRegisterLink',
    hintKey: 'reports.salesRegisterHint',
    roles: EVERYONE_BUT_TAILORS,
  },
]);

export function ReportsPage() {
  const { t } = useTranslation();
  const { role } = useAuth();

  const visible = REPORTS.filter((report) => (role ? report.roles.includes(role) : false));

  return (
    <>
      <PageHeader title={t('reports.title')} description={t('reports.description')} />

      <div className="flex flex-col gap-2">
        {role
          ? visible.map((report) => <ReportRow key={report.to} report={report} role={role} />)
          : null}
      </div>
    </>
  );
}

function ReportRow({ report, role }: { report: ReportLink; role: UserRole }) {
  const { t } = useTranslation();
  const { to, icon: Icon, titleKey, hintKey } = report;
  const inPlan = useHasModule(report.module?.(role) ?? 'core');

  return (
    <Link
      to={to}
      className="flex items-start gap-3 rounded-lg border border-border p-3 hover:bg-muted/50"
    >
      <Icon aria-hidden="true" className="mt-0.5 size-5 text-muted-foreground" />
      <span>
        <span className="flex flex-wrap items-center gap-2 text-sm font-semibold">
          {t(titleKey)}
          {inPlan ? null : <Badge variant="secondary">{t('settings.notInPlan')}</Badge>}
        </span>
        <span className="block text-sm text-muted-foreground">{t(hintKey)}</span>
      </span>
    </Link>
  );
}

export default ReportsPage;
